// inv/src/components/investor-page/modules/pRelatedInvestors.jsx
import { useState, useEffect, useContext, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../../config';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CountryFlag from '../../countryFlags/countryFlag';
import InvestorLogo from '../../data/InvestorLogo';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { DataContext } from '../../data/DataContext';
import CTA from '../../ui/CTA';
import { ExternalLink } from 'lucide-react';
import InvestorIndustries from '../../investor-display/InvestorIndustries';
import InvestorShortInfo from '../../investor-display/InvestorShortInfo';
import {
  buildInvestorProfilePath,
  getInvestorIndustries,
  getInvestorTypes,
} from '../../investor-display/helpers';

const RelatedInvestors = ({ investorId, currentCountry }) => {
  const { t, i18n } = useTranslation();
  const ssrData = useContext(DataContext);
  const ssrRelatedInvestors = ssrData?.relatedInvestors;
  const ssrShowCTA = ssrData?.showCTA;

  const [relatedInvestors, setRelatedInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ssrRelatedInvestors && ssrRelatedInvestors.length > 0) {
      setRelatedInvestors(ssrRelatedInvestors);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchRelatedInvestors = async () => {
      if (!investorId) return;

      if (ssrRelatedInvestors) {
        setRelatedInvestors([]);
        setLoading(true);
      }

      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`${API_URL}/investors-limited/${investorId}/related`, {
          params: { by: 'id' },
        });

        setRelatedInvestors(response.data.relatedInvestors || []);
      } catch (err) {
        console.error('Error fetching related investors:', err);
        setError('Failed to load related investors');
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedInvestors();
  }, [investorId]);

  if (loading) {
    return (
      <div className="w-full rounded-md bg-white p-6 shadow">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (relatedInvestors.length === 0) {
    return (
      <div className="w-full rounded-md bg-white p-6 shadow">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900  mb-2">
            {t('pageInvProf.relatedInvestors')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('pageInvProf.relatedInvestorsSubtitle', { country: currentCountry })}
          </p>
        </div>
        <CTA
          lng={i18n.language}
          customTitle={t('pageInvProf.ctaTitle')}
          customDescription={t('pageInvProf.ctaDescription')}
          customButtonText={t('pageInvProf.ctaButton')}
          customUrl={`${i18n.language === 'en' ? '' : `/${i18n.language}`}/pricing`}
        />
      </div>
    );
  }

  return (
    <div className="w-full rounded-md bg-white p-6 shadow">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900  mb-2">
          {t('pageInvProf.relatedInvestors')}
        </h3>
        <p className="text-sm text-gray-600">
          {t('pageInvProf.relatedInvestorsSubtitle', { country: currentCountry })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 pr-0 sm:pr-6">
        {relatedInvestors.map((inv, idx) => {
          const types = getInvestorTypes(inv);
          const industries = getInvestorIndustries(inv);
          const shortInfo = inv.short_info || inv.shortInfo;
          const country = inv.country;

          return (
            <Fragment key={inv._id || inv.id}>
              <div className="bg-white border border-stone-300 shadow-sm rounded-md p-6 flex flex-col h-full">
                <div className="flex items-start mb-4 gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <InvestorLogo
                      id={inv.id}
                      country={country}
                      className="flex-none w-6 h-6"
                      alt={`${inv.name} logo`}
                      width={24}
                      height={24}
                    />
                    <Link
                      to={buildInvestorProfilePath({ investor: inv, language: i18n.language })}
                      className="flex items-center gap-1 text-lg font-bold text-gray-900 hover:underline whitespace-normal break-words leading-tight"
                    >
                      <span>{inv.name}</span>
                      <ExternalLink size={14} className="shrink-0" />
                    </Link>
                  </div>
                  <div className="w-6 h-4 flex-none overflow-hidden">
                    <CountryFlag countryName={country} width={24} height={16} />
                  </div>
                </div>

                {types.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4 ">
                    {types.map((tpe, i) => (
                      <span key={i} className="text-sm">
                        {String(tpe).trim()}
                      </span>
                    ))}
                  </div>
                )}

                {industries.length > 0 && (
                  <InvestorIndustries
                    industries={industries}
                    t={t}
                    alwaysShowAll
                  />
                )}

                <div className="flex flex-col gap-1 mb-4">
                  {inv.leadDealsCount > 0 && (
                    <span className="text-xs text-gray-600">
                      {t(inv.leadDealsCount === 1 ? 'pageInvProf.leadDealsCount' : 'pageInvProf.leadDealsCount_plural', { count: inv.leadDealsCount })}
                    </span>
                  )}
                  {inv.participatedDealsCount > 0 && (
                    <span className="text-xs text-gray-600">
                      {t(inv.participatedDealsCount === 1 ? 'pageInvProf.participatedDealsCount' : 'pageInvProf.participatedDealsCount_plural', { count: inv.participatedDealsCount })}
                    </span>
                  )}
                </div>

                {shortInfo && (
                  <InvestorShortInfo
                    shortInfo={shortInfo}
                    t={t}
                    maxLength={200}
                    className="text-xs text-stone-700"
                    buttonClassName="ml-2 inline-flex cursor-pointer items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-full"
                  />
                )}
              </div>

              {idx === 5 && ssrShowCTA !== false && (
                <div className="bg-white border border-stone-300 shadow-sm rounded-md p-6 flex flex-col h-full">
                  <CTA
                    lng={i18n.language}
                    customTitle={t('pageInvProf.ctaTitle')}
                    customDescription={t('pageInvProf.ctaDescription')}
                    customButtonText={t('pageInvProf.ctaButton')}
                    customUrl={`${i18n.language === 'en' ? '' : `/${i18n.language}`}/pricing`}
                  />
                </div>
              )}
            </Fragment>
          );
        })}

        {relatedInvestors.length < 6 && ssrShowCTA !== false && (
          <div className="bg-white border border-stone-300 shadow-sm rounded-md p-6 flex flex-col h-full">
            <CTA
              lng={i18n.language}
              customTitle={t('pageInvProf.ctaTitle')}
              customDescription={t('pageInvProf.ctaDescription')}
              customButtonText={t('pageInvProf.ctaButton')}
              customUrl={`${i18n.language === 'en' ? '' : `/${i18n.language}`}/pricing`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatedInvestors;
